// Phase MHM-3 · Inventory — items + movements + analytics.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Plus, X, ArrowDown, ArrowUp, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

function MovementModal({ open, onClose, item, type, onCreated }) {
  const [qty, setQty] = useState(0);
  const [reason, setReason] = useState("");
  if (!open || !item) return null;
  const submit = async () => {
    if (qty <= 0) return toast.error("qty > 0");
    if (!reason) return toast.error("Reason required");
    try {
      await api.post(`/hub/inventory/items/${item.item_id}/movement`,
                     { type, quantity: Number(qty), reason });
      toast.success(`${type === "in" ? "Stock received" : type === "out" ? "Stock consumed" : "Adjusted"}`);
      setQty(0); setReason("");
      onCreated(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.userMessage || "Failed");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mhm-inv-movement-modal">
      <div className="bg-white rounded-2xl p-5 max-w-md w-full">
        <div className="flex justify-between mb-3"><h3 className="font-semibold capitalize">{type} stock · {item.name}</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="space-y-3 text-sm">
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                 placeholder="Quantity" data-testid="mhm-inv-movement-qty"
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Reason" data-testid="mhm-inv-movement-reason"
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          <div className="text-xs text-slate-500">Current stock: <b>{item.current_stock}</b> {item.unit}</div>
        </div>
        <button onClick={submit} data-testid="mhm-inv-movement-submit"
                className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-1.5 text-sm">
          Confirm
        </button>
      </div>
    </div>
  );
}

function ItemModal({ open, onClose, onCreated }) {
  const [d, setD] = useState({ sku: "", name: "", unit: "pcs",
                                current_stock: 0, reorder_point: 0,
                                supplier: "", notes: "" });
  if (!open) return null;
  const submit = async () => {
    if (!d.sku || !d.name) return toast.error("SKU + name required");
    try {
      await api.post("/hub/inventory/items", d);
      toast.success("Item created"); onCreated(); onClose();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mhm-inv-item-modal">
      <div className="bg-white rounded-2xl p-5 max-w-md w-full">
        <div className="flex justify-between mb-3"><h3 className="font-semibold">New inventory item</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <input placeholder="SKU" value={d.sku} onChange={(e) => setD({...d, sku: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5"/>
          <input placeholder="Name" value={d.name} onChange={(e) => setD({...d, name: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5"/>
          <select value={d.unit} onChange={(e) => setD({...d, unit: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5">
            {["pcs", "meters", "kg", "rolls"].map(u => <option key={u}>{u}</option>)}
          </select>
          <input type="number" placeholder="Current stock" value={d.current_stock} onChange={(e) => setD({...d, current_stock: Number(e.target.value)})} className="border border-slate-200 rounded-lg px-2 py-1.5"/>
          <input type="number" placeholder="Reorder point" value={d.reorder_point} onChange={(e) => setD({...d, reorder_point: Number(e.target.value)})} className="border border-slate-200 rounded-lg px-2 py-1.5"/>
          <input placeholder="Supplier" value={d.supplier} onChange={(e) => setD({...d, supplier: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5"/>
        </div>
        <button onClick={submit} className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-1.5 text-sm" data-testid="mhm-inv-item-submit">Create</button>
      </div>
    </div>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [movement, setMovement] = useState(null); // {item, type}
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, an] = await Promise.all([
        api.get("/hub/inventory/items"),
        api.get("/hub/inventory/usage-analytics?range=30d"),
      ]);
      setItems(it.data.rows || []);
      setAnalytics(an.data.items || []);
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openHistory = async (item) => {
    setHistoryFor(item);
    try {
      const { data } = await api.get(`/hub/inventory/items/${item.item_id}/movements`);
      setHistory(data.rows || []);
    } catch { setHistory([]); }
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-inventory-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-inventory-title">
              Inventory
            </h1>
            <p className="text-sm text-slate-500">{items.length} items · {items.filter(i => i.low_stock).length} low stock</p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={load} data-testid="mhm-inventory-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
            <button onClick={() => setShowItemModal(true)} data-testid="mhm-inventory-add-item"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
              <Plus className="h-4 w-4" /> Add item
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mhm-inventory-table">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">SKU</th>
                <th className="text-left">Name</th>
                <th className="text-left">Unit</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Reorder pt.</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && !loading && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No items</td></tr>}
              {items.map((it) => (
                <tr key={it.item_id} data-testid={`mhm-inv-row-${it.sku}`} className={it.low_stock ? "bg-rose-50" : ""}>
                  <td className="p-2 font-mono text-xs">{it.sku}</td>
                  <td>{it.name}{it.low_stock && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-rose-200 text-rose-800" data-testid={`mhm-inv-lowstock-${it.sku}`}>LOW</span>}</td>
                  <td>{it.unit}</td>
                  <td className="text-right font-mono">{it.current_stock}</td>
                  <td className="text-right text-xs text-slate-500">{it.reorder_point}</td>
                  <td className="text-right text-xs space-x-1">
                    <button onClick={() => setMovement({ item: it, type: "in" })}
                            data-testid={`mhm-inv-receive-${it.sku}`}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1">
                      <ArrowDown className="h-3 w-3" /> Receive
                    </button>
                    <button onClick={() => setMovement({ item: it, type: "out" })}
                            data-testid={`mhm-inv-consume-${it.sku}`}
                            className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded inline-flex items-center gap-1">
                      <ArrowUp className="h-3 w-3" /> Consume
                    </button>
                    <button onClick={() => openHistory(it)}
                            className="px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded inline-flex items-center gap-1">
                      <ListOrdered className="h-3 w-3" /> History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Usage analytics */}
        {analytics.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="mhm-inv-analytics">
            <h3 className="font-semibold mb-2 text-slate-800">Usage analytics · 30d</h3>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left p-1">Item</th><th className="text-right">Usage (30d)</th><th className="text-right">Avg/day</th><th className="text-right">Days to zero</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {analytics.map((a) => (
                  <tr key={a.item_id}>
                    <td className="p-1">{a.name}</td>
                    <td className="text-right">{a.usage_total} {a.unit}</td>
                    <td className="text-right">{a.avg_daily_usage}</td>
                    <td className={`text-right ${a.days_until_zero != null && a.days_until_zero < 14 ? "text-rose-600 font-bold" : ""}`}>
                      {a.days_until_zero != null ? `${a.days_until_zero} days` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Movement history drawer */}
        {historyFor && (
          <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setHistoryFor(null)} data-testid="mhm-inv-history-drawer">
            <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full h-full overflow-y-auto shadow-xl">
              <div className="p-4 border-b border-slate-200 flex justify-between">
                <h3 className="font-semibold">History · {historyFor.name}</h3>
                <button onClick={() => setHistoryFor(null)}><X className="h-4 w-4" /></button>
              </div>
              <div className="p-4 space-y-2">
                {history.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No movements yet</div>}
                {history.map((m) => (
                  <div key={m.movement_id} className="border border-slate-200 rounded-lg p-2 text-xs">
                    <div className="flex justify-between">
                      <span className="capitalize font-semibold">{m.type}</span>
                      <span className="text-slate-500">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div>{m.quantity} ({m.stock_before} → {m.stock_after})</div>
                    <div className="text-slate-500">{m.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <ItemModal open={showItemModal} onClose={() => setShowItemModal(false)} onCreated={load}/>
      <MovementModal open={!!movement} onClose={() => setMovement(null)}
                     onCreated={load} item={movement?.item} type={movement?.type}/>
    </RoleGate>
  );
}
